import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CanvasService, CanvasCourse, CanvasPage, CanvasFile } from 'src/app/_services/canvas.service';

@Component({
  selector: 'tangram-create-from-canvas',
  templateUrl: './create-from-canvas.component.html',
  styleUrls: ['./create-from-canvas.component.scss'],
})
export class CreateFromCanvasComponent implements OnInit {
  canvasStep = 1;
  canvasConnected = false;
  canvasConnecting = false;
  canvasLoadingCourses = false;
  canvasLoadingContent = false;
  canvasImporting = false;
  canvasInstanceUrl = 'https://canvas.instructure.com';
  canvasAccessToken = '';
  canvasUserName: string | null = null;
  canvasConnectError = '';
  canvasCourses: CanvasCourse[] = [];
  canvasSelectedCourse: CanvasCourse | null = null;
  canvasPages: (CanvasPage & { selected?: boolean })[] = [];
  canvasFiles: (CanvasFile & { selected?: boolean })[] = [];

  constructor(
    private canvasService: CanvasService,
    private toaster: ToastrService,
    private router: Router,
  ) {}

  ngOnInit() { this.checkCanvasStatus(); }

  get selectedCanvasPages(): any[] { return (this.canvasPages || []).filter((p) => p.selected); }
  get selectedCanvasFiles(): any[] { return (this.canvasFiles || []).filter((f) => f.selected); }

  get isCanvasStepValid(): boolean {
    if (this.canvasStep === 1) return !!this.canvasSelectedCourse;
    if (this.canvasStep === 2) return this.canvasPages.some((p) => p.selected) || this.canvasFiles.some((f) => f.selected);
    return true;
  }

  setCanvasStep(step: number) {
    if (step >= 1 && step <= 4 && (step < this.canvasStep || this.isCanvasStepValid)) {
      this.canvasStep = step;
    }
  }
  nextCanvasStep() { if (this.isCanvasStepValid && this.canvasStep < 4) this.canvasStep++; }
  prevCanvasStep() { if (this.canvasStep > 1) this.canvasStep--; }

  selectCanvasCourse(course: CanvasCourse) {
    this.canvasSelectedCourse = course;
    this.loadCanvasCourseContent(course.id);
  }
  toggleCanvasPage(page: any) { page.selected = !page.selected; }
  toggleCanvasFile(file: any) { file.selected = !file.selected; }
  selectAllCanvasPages() { this.canvasPages.forEach((p) => p.selected = true); }
  deselectAllCanvasPages() {
    this.canvasPages.forEach((p) => p.selected = false);
    this.canvasFiles.forEach((f) => f.selected = false);
  }

  checkCanvasStatus() {
    this.canvasService.getStatus().subscribe(
      (res) => {
        if (res && res.data && res.data.connected) {
          this.canvasConnected = true;
          this.canvasUserName = res.data.canvasUserName;
          this.canvasInstanceUrl = res.data.instanceUrl || this.canvasInstanceUrl;
          this.loadCanvasCourses();
        } else {
          this.canvasConnected = false;
        }
      },
      () => { this.canvasConnected = false; },
    );
  }

  connectCanvas() {
    this.canvasConnectError = '';
    if (!this.canvasInstanceUrl || !this.canvasAccessToken) {
      this.canvasConnectError = 'Enter both Canvas URL and access token.';
      return;
    }
    this.canvasConnecting = true;
    this.canvasService.connect(this.canvasInstanceUrl, this.canvasAccessToken).subscribe(
      (res: any) => {
        this.canvasConnecting = false;
        if (res && res.success) {
          this.canvasConnected = true;
          this.canvasUserName = res.data && res.data.canvasUserName;
          this.canvasAccessToken = '';
          this.toaster.success('Canvas account connected');
          this.loadCanvasCourses();
        } else {
          this.canvasConnectError = (res && res.message) || 'Connection failed';
        }
      },
      (err) => {
        this.canvasConnecting = false;
        this.canvasConnectError = (err && err.error && err.error.message) || 'Connection failed. Check your URL and token.';
      },
    );
  }

  disconnectCanvas() {
    this.canvasService.disconnect().subscribe(() => {
      this.canvasConnected = false;
      this.canvasCourses = [];
      this.canvasSelectedCourse = null;
      this.canvasPages = [];
      this.canvasFiles = [];
      this.canvasStep = 1;
      this.canvasUserName = null;
      this.toaster.info('Canvas account disconnected');
    });
  }

  loadCanvasCourses() {
    this.canvasLoadingCourses = true;
    this.canvasService.listCourses().subscribe(
      (res) => {
        this.canvasLoadingCourses = false;
        this.canvasCourses = (res && res.data) || [];
        if (this.canvasCourses.length === 0) {
          this.toaster.info('No teacher courses found on your Canvas account.');
        }
      },
      (err) => {
        this.canvasLoadingCourses = false;
        const msg = (err && err.error && err.error.message) || 'Failed to load Canvas courses';
        this.toaster.error(msg);
        if (err && err.status === 412) this.canvasConnected = false;
      },
    );
  }

  loadCanvasCourseContent(courseId: number) {
    this.canvasLoadingContent = true;
    this.canvasPages = [];
    this.canvasFiles = [];
    this.canvasService.listPages(courseId).subscribe(
      (res) => { this.canvasPages = ((res && res.data) || []).map((p) => ({ ...p, selected: false })); },
      () => {},
    );
    this.canvasService.listFiles(courseId).subscribe(
      (res) => {
        this.canvasLoadingContent = false;
        this.canvasFiles = ((res && res.data) || []).map((f) => ({ ...f, selected: false }));
      },
      () => { this.canvasLoadingContent = false; },
    );
  }

  createTangramFromCanvas() {
    if (!this.canvasSelectedCourse) return;
    const pages = this.selectedCanvasPages.map((p) => ({ url: p.url, title: p.title }));
    const files = this.selectedCanvasFiles.map((f) => ({ url: f.url, displayName: f.displayName, contentType: f.contentType }));
    if (pages.length === 0 && files.length === 0) {
      this.toaster.error('Select at least one page or file to import');
      return;
    }
    this.canvasImporting = true;
    this.canvasService.importCourse({
      courseId: this.canvasSelectedCourse.id,
      courseName: this.canvasSelectedCourse.name,
      pages,
      files,
    }).subscribe(
      (res: any) => {
        this.canvasImporting = false;
        if (res && res.success) {
          this.toaster.success(`Imported ${res.data.mediaCount} items from Canvas`);
          this.router.navigate(['/share-tangram'], { queryParams: { pathwayId: res.data.pathwayId } });
        } else {
          this.toaster.error((res && res.message) || 'Import failed');
        }
      },
      (err) => {
        this.canvasImporting = false;
        const msg = (err && err.error && err.error.message) || 'Failed to import Canvas content';
        this.toaster.error(msg);
      },
    );
  }
}
